import { useState, type FormEvent } from 'react';
import type {
  BillingFrequency,
  CreateOpportunityProductInput,
  OpportunityProduct,
  Product,
} from '@catalyst-psa/shared';
import { BILLING_FREQUENCIES } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { SelectField, TextField } from '../../components/ui/Field.js';
import { useProductOptions } from '../products/api.js';
import { ProductPicker } from '../products/ProductPicker.js';
import {
  useCreateOpportunityProduct,
  useDeleteOpportunityProduct,
  useOpportunityProducts,
  useUpdateOpportunityProduct,
} from './api.js';

interface OpportunityProductsSectionProps {
  opportunityId: string;
  /** The opportunity's "Type" (service category); used to filter the product dropdown. */
  opportunityServiceCategoryId?: string | null;
  canEdit: boolean;
}

interface ProductFormState {
  productId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  billingFrequency: BillingFrequency;
}

const EMPTY_PRODUCT: ProductFormState = {
  productId: '',
  description: '',
  quantity: '1',
  unitPrice: '0',
  billingFrequency: 'monthly',
};

const FREQUENCY_LABELS: Record<BillingFrequency, string> = {
  monthly: 'Monthly',
  yearly: 'Yearly',
};

const FREQUENCY_OPTIONS = BILLING_FREQUENCIES.map((value) => ({
  value,
  label: FREQUENCY_LABELS[value],
}));

function toFormState(line: OpportunityProduct): ProductFormState {
  return {
    productId: line.productId ?? '',
    description: line.description,
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    billingFrequency: line.billingFrequency,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function OpportunityProductsSection({
  opportunityId,
  opportunityServiceCategoryId,
  canEdit,
}: OpportunityProductsSectionProps): JSX.Element {
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<ProductFormState>(EMPTY_PRODUCT);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const productsQuery = useOpportunityProducts(opportunityId);
  const catalogQuery = useProductOptions();
  const createMutation = useCreateOpportunityProduct();
  const updateMutation = useUpdateOpportunityProduct();
  const deleteMutation = useDeleteOpportunityProduct();

  const lines = productsQuery.data ?? [];
  const catalog = catalogQuery.data ?? [];
  const isFormOpen = editing !== null;
  const isCreate = editing === 'new';
  const mutation = isCreate ? createMutation : updateMutation;
  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError = mutation.isError
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : 'Failed to save product.'
    : null;

  // The dropdown normally shows only products whose service category matches the
  // opportunity type. Typing in the picker's search box finds ANY product regardless of type.
  const suggestedProducts = opportunityServiceCategoryId
    ? catalog.filter((p) => p.serviceCategoryId === opportunityServiceCategoryId)
    : catalog;

  const total = lines.reduce(
    (sum, line) => sum + Number(line.quantity) * Number(line.unitPrice),
    0,
  );
  const lineAmount = (line: OpportunityProduct): number =>
    Number(line.quantity) * Number(line.unitPrice);
  const nonRecurringTotal = lines
    .filter((l) => !l.recurring)
    .reduce((sum, l) => sum + lineAmount(l), 0);
  const recurringMonthlyTotal = lines
    .filter((l) => l.recurring && l.billingFrequency === 'monthly')
    .reduce((sum, l) => sum + lineAmount(l), 0);
  const recurringYearlyTotal = lines
    .filter((l) => l.recurring && l.billingFrequency === 'yearly')
    .reduce((sum, l) => sum + lineAmount(l), 0);

  const openCreate = (): void => {
    setForm(EMPTY_PRODUCT);
    setEditing('new');
    createMutation.reset();
    updateMutation.reset();
  };

  const openEdit = (line: OpportunityProduct): void => {
    setForm(toFormState(line));
    setEditing(line.id);
    createMutation.reset();
    updateMutation.reset();
  };

  const closeForm = (): void => {
    setEditing(null);
  };

  const handleProductChange = (productId: string): void => {
    const product: Product | undefined = catalog.find((p) => p.id === productId);
    setForm((f) => ({
      ...f,
      productId,
      description: product ? product.name : f.description,
      unitPrice: product ? product.unitPrice : f.unitPrice,
    }));
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const base: CreateOpportunityProductInput = {
      productId: form.productId ? form.productId : null,
      description: form.description.trim(),
      quantity: form.quantity.trim() ? form.quantity.trim() : '1',
      unitPrice: form.unitPrice.trim() ? form.unitPrice.trim() : '0',
      billingFrequency: form.billingFrequency,
    };

    if (isCreate) {
      createMutation.mutate({ opportunityId, input: base }, { onSuccess: closeForm });
    } else if (editing) {
      updateMutation.mutate(
        { opportunityId, productLineId: editing, input: base },
        { onSuccess: closeForm },
      );
    }
  };

  const handleDelete = (productLineId: string): void => {
    deleteMutation.mutate(
      { opportunityId, productLineId },
      { onSuccess: () => setConfirmId(null) },
    );
  };

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
        <span className="text-sm font-medium text-slate-700">Products</span>
        {canEdit && !isFormOpen ? (
          <Button variant="secondary" onClick={openCreate} className="px-2.5 py-1 text-xs">
            Add product
          </Button>
        ) : null}
      </div>

      <div className="divide-y divide-slate-100">
        {productsQuery.isLoading ? (
          <p className="px-3 py-2 text-sm text-slate-500">Loading products…</p>
        ) : lines.length === 0 && !isFormOpen ? (
          <p className="px-3 py-2 text-sm text-slate-500">No products yet.</p>
        ) : (
          lines.map((line) => (
            <div key={line.id} className="flex items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0">
                <span className="flex items-center gap-2 truncate text-sm font-medium text-slate-900">
                  <span className="truncate">{line.description || '—'}</span>
                  {line.recurring ? <Badge tone="blue">Recurring</Badge> : null}
                  {line.recurring ? (
                    <Badge tone="amber">{FREQUENCY_LABELS[line.billingFrequency]}</Badge>
                  ) : null}
                </span>
                <div className="truncate text-xs text-slate-500">
                  {line.quantity} × {currency.format(Number(line.unitPrice))} ={' '}
                  {currency.format(Number(line.quantity) * Number(line.unitPrice))}
                  {line.recurring ? ' / cycle' : ''}
                </div>
              </div>
              {canEdit ? (
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    onClick={() => openEdit(line)}
                  >
                    Edit
                  </Button>
                  {confirmId === line.id ? (
                    <>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                        onClick={() => handleDelete(line.id)}
                        isLoading={deleteMutation.isPending}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        onClick={() => setConfirmId(null)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50"
                      onClick={() => setConfirmId(line.id)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>

      {lines.length > 0 ? (
        <div className="space-y-1 border-t border-slate-200 px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium text-slate-700">Total</span>
            <span className="font-semibold text-slate-900">{currency.format(total)}</span>
          </div>
          <div className="flex items-center justify-between text-slate-500">
            <span>Non-recurring</span>
            <span className="tabular-nums">{currency.format(nonRecurringTotal)}</span>
          </div>
          {recurringMonthlyTotal > 0 ? (
            <div className="flex items-center justify-between text-slate-500">
              <span>Recurring (Monthly)</span>
              <span className="tabular-nums">{currency.format(recurringMonthlyTotal)}</span>
            </div>
          ) : null}
          {recurringYearlyTotal > 0 ? (
            <div className="flex items-center justify-between text-slate-500">
              <span>Recurring (Yearly)</span>
              <span className="tabular-nums">{currency.format(recurringYearlyTotal)}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {isFormOpen ? (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 border-t border-slate-200 p-3">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}
          <ProductPicker
            label="Product"
            products={catalog}
            suggestedProducts={suggestedProducts}
            value={form.productId}
            emptyLabel="No product (ad-hoc)"
            error={fieldErrors.productId}
            onChange={handleProductChange}
          />
          {opportunityServiceCategoryId ? (
            <p className="-mt-1 text-xs text-slate-500">
              Showing products for this opportunity type. Search to find any product.
            </p>
          ) : null}
          <TextField
            label="Description"
            required
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            error={fieldErrors.description}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Quantity"
              type="number"
              min={0}
              step="0.01"
              required
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              error={fieldErrors.quantity}
            />
            <TextField
              label="Unit price"
              type="number"
              min={0}
              step="0.01"
              required
              value={form.unitPrice}
              onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))}
              error={fieldErrors.unitPrice}
            />
          </div>
          <SelectField
            label="Billing frequency"
            value={form.billingFrequency}
            options={FREQUENCY_OPTIONS}
            error={fieldErrors.billingFrequency}
            onChange={(e) =>
              setForm((f) => ({ ...f, billingFrequency: e.target.value as BillingFrequency }))
            }
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={closeForm}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={mutation.isPending}>
              {isCreate ? 'Add product' : 'Save product'}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
